import { useApolloClient } from "@apollo/client";
import { getNameWithCopy } from "@msys/common";
import { MenuItemWithIcon } from "@msys/ui";
import { ControlPointDuplicate as ControlPointDuplicateIcon } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../../../auth/useUserData.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { useUnsubscribeFromQuoteTemplateMutation } from "../quote/buttons/QuoteTemplateUnsubscribeButton.generated.js";
import { useDuplicateQuoteTemplateMutation } from "../quote/TemplateQuotes.generated.js";
import { DuplicateTemplateMenuItem_QuoteTemplateFragment } from "./DuplicateTemplateMenuItem.generated.js";

interface Props {
  doc: DuplicateTemplateMenuItem_QuoteTemplateFragment;
  pathToDocList: string;
  onDuplicateRefetchQueries?: string[];
}

export const DuplicateTemplateMenuItem = ({
  doc,
  pathToDocList,
  onDuplicateRefetchQueries,
}: Props) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const viewer = useUserData().currentUser!;

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);

  const { t } = useTranslate(["Quote", "Global", "Templates"]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [
    duplicateQuoteTemplateMutation,
    { loading: duplicateQuoteTemplateLoading },
  ] = useDuplicateQuoteTemplateMutation({
    client,
  });
  const [unsubscribe, { loading: unsubscribeLoading }] =
    useUnsubscribeFromQuoteTemplateMutation({
      client,
    });

  const duplicateQuoteTemplate = async () => {
    const isSubscribed = Boolean(
      doc.owningSystemOrganisationId !== viewer.organisation.id &&
      doc.templateSharing?.subscribedToTemplate?.subscribedVersionNumber
    );
    const canUnsubscribe = !doc.usedInOtherTemplates;

    const result = await confirmProcessRef.current!.startConfirmProcess({
      title: t("Duplicate template “{name}”", {
        ns: "Templates",
        name: doc.title,
      }),
      text: t("Are you sure you want to duplicate this template?", {
        ns: "Templates",
      }),
    });
    if (!result) return;
    try {
      const res = await duplicateQuoteTemplateMutation({
        variables: {
          input: {
            title: getNameWithCopy(
              doc.title,
              t("copy", {
                ns: "Global",
              })
            ),
            quoteTemplateId: doc.id,
          },
        },
        refetchQueries: onDuplicateRefetchQueries,
        awaitRefetchQueries: true,
      });

      const newQuoteTemplateId =
        res?.data?.duplicateQuoteTemplate?.quoteTemplate?.id;

      if (!newQuoteTemplateId) return;

      navigate(`${pathToDocList}/${newQuoteTemplateId}/latest`);
      enqueueSnackbar(
        t("Quote template duplicated", {
          ns: "Quote",
        })
      );

      if (isSubscribed && canUnsubscribe) {
        const name = doc.title;
        const key = enqueueSnackbar(
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography>
              {t("Unsubscribe from original template?", { ns: "Templates" })}
            </Typography>
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={async () => {
                await unsubscribe({
                  variables: { input: { quoteTemplateId: doc.id } },
                });
                closeSnackbar(key);
                enqueueSnackbar(
                  t("Unsubscribed from template “{name}”", {
                    ns: "Templates",
                    name,
                  })
                );
              }}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              loading={unsubscribeLoading}
            >
              {t("Unsubscribe", { ns: "Templates" })}
            </Button>
          </Stack>,
          { variant: "info", persist: true }
        );
      }
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  return (
    <>
      <MenuItemWithIcon
        icon={<ControlPointDuplicateIcon />}
        onClick={duplicateQuoteTemplate}
        disabled={duplicateQuoteTemplateLoading}
      >
        {t("Duplicate template", { ns: "Quote" })}
      </MenuItemWithIcon>
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );
};
